import type { FC } from 'react';
import { useEffect, useState } from 'react';

import type {
  XingletComponent,
  XingletLoaderProps,
} from '@xing-com/crate-xinglet';
import type { InternalHost } from '@xing-com/crate-xinglet/internal';

type InternalXingletLoaderProps = XingletLoaderProps & {
  host: InternalHost;
};

// todo: rewrite to use React.Suspense/React.lazy for better UX
// this will need a bit of extra-work since React.lazy is currently
// not very dynamic and could lead to memory-leaks if recreated during
// re-render
export const InternalXingletLoader: FC<InternalXingletLoaderProps> = ({
  host,
  name,
  fallback = null,
  ...rest
}) => {
  const [state, setState] = useState<{
    name: string;
    error?: unknown;
    Component?: XingletComponent;
  }>({ name });

  const ssr = host.runtime.metadataMap[name]?.ssr ?? false;
  const skipRendering = host.isServer && !ssr;
  const runtime = host.runtime;
  const eventualComponent = skipRendering
    ? undefined
    : runtime.loadXingletComponent(host, name);

  const currentName = state.name;
  const error = eventualComponent?.error ?? state.error;
  const Component = eventualComponent?.value ?? state.Component;

  useEffect(() => {
    if (
      currentName === name &&
      (Boolean(Component) || Boolean(error) || !runtime.autoRerender)
    ) {
      return;
    }

    let mounted = true;

    eventualComponent?.then(
      (Component) => {
        if (!mounted) return;

        setState({ name, Component });
      },
      (error) => {
        if (!mounted) return;

        setState({ name, error });
      }
    );

    return () => {
      mounted = false;
    };
  }, [currentName, name, eventualComponent, runtime, error, Component]);

  useEffect(() => {
    return runtime.events.on('xinglet-reloaded', (otherName) => {
      if (otherName !== name || !Component) return;
      setState({ name });
    });
  }, [Component, runtime, name]);

  if (error) {
    if (error instanceof Error && !error.message.startsWith(`${name}:`)) {
      error.message = `${name}: ${error.message}`;
    }

    throw error;
  }

  if (!Component && !skipRendering) {
    runtime.events.emit('xinglet-awaited', name);
  }

  return Component ? <Component {...rest} /> : fallback;
};
